import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, NotePencil, ArrowLeft, User, Package, MapPin, Phone as PhoneIcon, EnvelopeSimple, CurrencyDollar, FilePdf, PaperPlaneTilt, FloppyDisk, Trash } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdminNav from './AdminNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRFQs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [partsWithPrices, setPartsWithPrices] = useState([]); // Parts with unit prices for display
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [notes, setNotes] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [showDialog, setShowDialog] = useState(false);

  // Bulk-delete state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Quote state
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteLineItems, setQuoteLineItems] = useState([]);
  const [quotePaymentTerms, setQuotePaymentTerms] = useState('');
  const [quoteShippingCost, setQuoteShippingCost] = useState(0);
  const [quoteTax, setQuoteTax] = useState(0);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteValidityDays, setQuoteValidityDays] = useState(1);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [existingQuotes, setExistingQuotes] = useState([]);
  
  // Email state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailQuoteId, setEmailQuoteId] = useState(null);
  
  // File viewer state
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  
  // Quote document viewer state
  const [showQuoteViewer, setShowQuoteViewer] = useState(false);
  const [viewingQuote, setViewingQuote] = useState(null);
  
  // Company contact info (from landing page)
  const companyContact = {
    name: "Geoff Simon",
    title: "Channel Sales Manager",
    phone_office: "949-485-5525 ext 814",
    phone_direct: "949-388-2740",
    phone_cell: "949-680-0291",
    email: "simon@memory.net",
    linkedin: "https://www.linkedin.com/in/geoffsimon",
    guarantees: [
      "100% GUARANTEED COMPATIBLE",
      "QUICK ADVANCE REPLACEMENT",
      "EXPERT TECHNICAL SUPPORT"
    ]
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadRFQs();
  }, [statusFilter]);

  const loadRFQs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API_URL}/api/rfq${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRfqs(response.data.rfqs || []);
      setSelectedIds(new Set()); // clear selection on reload
    } catch (error) {
      toast.error('Failed to load RFQs');
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === rfqs.length && rfqs.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rfqs.map(r => r.id).filter(Boolean)));
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.post(`${API_URL}/api/admin/rfqs/bulk-delete`,
        { ids: Array.from(selectedIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${r.data.deleted} RFQ${r.data.deleted === 1 ? '' : 's'}`);
      setConfirmDelete(false);
      setSelectedIds(new Set());
      loadRFQs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const updateRFQStatus = async (rfqId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      const data = { status };
      if (status === 'Reschedule' && rescheduleDate) {
        data.reschedule_date = rescheduleDate;
      }
      if (notes) {
        data.notes = notes;
      }

      await axios.put(`${API_URL}/api/rfq/${rfqId}/status`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`RFQ status updated to ${status}`);
      setNotes('');
      setRescheduleDate('');
      setShowDialog(false);
      loadRFQs();
    } catch (error) {
      toast.error('Failed to update RFQ status');
    }
  };

  const openRFQDialog = async (rfq) => {
    setSelectedRFQ(rfq);
    setPartsWithPrices([]); // Reset parts with prices
    setNotes('');
    setRescheduleDate('');
    setShowDialog(true);
    setShowQuoteForm(false);
    
    // Fetch RFQ with unit prices from inventory
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/rfq/${rfq.id}/with-prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rfqWithPrices = response.data;
      
      // Store parts with prices for display in Selected Parts section
      if (rfqWithPrices.selected_parts && rfqWithPrices.selected_parts.length > 0) {
        setPartsWithPrices(rfqWithPrices.selected_parts);
        
        // Initialize quote line items from selected parts with unit prices
        const items = rfqWithPrices.selected_parts.map(part => {
          // Parse unit_price - could be a number, string number, or "CALL"
          let suggestedPrice = 0;
          const priceStr = part.unit_price || 'CALL';
          if (priceStr !== 'CALL' && !isNaN(parseFloat(priceStr))) {
            suggestedPrice = parseFloat(priceStr);
          }
          
          return {
            part_number: part.part_number,
            description: part.description,
            condition: part.condition,
            taa: part.taa,
            requested_qty: part.requested_qty,
            quoted_qty: part.requested_qty,
            unit_price: suggestedPrice,
            inventory_price: priceStr, // Store original price/CALL for display
            line_total: suggestedPrice * part.requested_qty,
            available_qty_at_selection: part.available_qty_at_selection
          };
        });
        setQuoteLineItems(items);
      } else {
        // No parts selected - initialize with one empty line for manual entry
        setQuoteLineItems([{
          part_number: '',
          description: `Qty ${rfq.quantity || ''} ${rfq.memory_type || ''} ${rfq.capacity || ''} ${rfq.speed || ''}`.trim() || 'Manual Entry',
          condition: 'NEW',
          taa: 'No',
          requested_qty: rfq.quantity || 1,
          quoted_qty: rfq.quantity || 1,
          unit_price: 0,
          inventory_price: 'CALL',
          line_total: 0
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch RFQ with prices:', error);
      // Fallback to original behavior without prices
      if (rfq.selected_parts && rfq.selected_parts.length > 0) {
        const items = rfq.selected_parts.map(part => ({
          part_number: part.part_number,
          description: part.description,
          condition: part.condition,
          taa: part.taa,
          requested_qty: part.requested_qty,
          quoted_qty: part.requested_qty,
          unit_price: 0,
          inventory_price: 'CALL',
          line_total: 0
        }));
        setQuoteLineItems(items);
      } else {
        setQuoteLineItems([{
          part_number: '',
          description: `Qty ${rfq.quantity || ''} ${rfq.memory_type || ''} ${rfq.capacity || ''} ${rfq.speed || ''}`.trim() || 'Manual Entry',
          condition: 'NEW',
          taa: 'No',
          requested_qty: rfq.quantity || 1,
          quoted_qty: rfq.quantity || 1,
          unit_price: 0,
          inventory_price: 'CALL',
          line_total: 0
        }]);
      }
    }
    
    // Reset quote form
    setQuotePaymentTerms('');
    setQuoteShippingCost(0);
    setQuoteTax(0);
    setQuoteNotes('');
    setQuoteValidityDays(30);
    
    // Load existing quotes for this RFQ
    loadExistingQuotes(rfq.id);
  };

  const loadExistingQuotes = async (rfqId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/quotes/rfq/${rfqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingQuotes(response.data.quotes || []);
    } catch (error) {
      console.error('Failed to load quotes:', error);
      setExistingQuotes([]);
    }
  };

  const updateLineItemPrice = (index, price) => {
    const updated = [...quoteLineItems];
    updated[index].unit_price = parseFloat(price) || 0;
    updated[index].line_total = updated[index].quoted_qty * updated[index].unit_price;
    setQuoteLineItems(updated);
  };

  const updateLineItemQty = (index, qty) => {
    const updated = [...quoteLineItems];
    updated[index].quoted_qty = parseInt(qty) || 0;
    updated[index].line_total = updated[index].quoted_qty * updated[index].unit_price;
    setQuoteLineItems(updated);
  };

  const updateLineItemField = (index, field, value) => {
    const updated = [...quoteLineItems];
    updated[index][field] = value;
    setQuoteLineItems(updated);
  };

  const addLineItem = () => {
    setQuoteLineItems([...quoteLineItems, {
      part_number: '',
      description: '',
      condition: 'NEW',
      taa: 'No',
      requested_qty: 1,
      quoted_qty: 1,
      unit_price: 0,
      line_total: 0
    }]);
  };

  const removeLineItem = (index) => {
    if (quoteLineItems.length > 1) {
      const updated = [...quoteLineItems];
      updated.splice(index, 1);
      setQuoteLineItems(updated);
    }
  };

  const calculateSubtotal = () => {
    return quoteLineItems.reduce((sum, item) => sum + item.line_total, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + parseFloat(quoteShippingCost || 0) + parseFloat(quoteTax || 0);
  };

  // Email functions
  const openEmailDialog = (quote = null) => {
    if (quote) {
      setEmailQuoteId(quote.id);
      setEmailTo(quote.contact_email || selectedRFQ?.email || '');
      setEmailSubject(`Quote ${quote.quote_number} from Server Memory RFQ`);
      setEmailBody('');
    } else if (selectedRFQ) {
      setEmailQuoteId(null);
      setEmailTo(selectedRFQ.email || '');
      setEmailSubject(`RE: Your Memory RFQ from Server Memory RFQ`);
      setEmailBody('');
    }
    setEmailCc('');
    setEmailBcc('');
    setShowEmailDialog(true);
  };

  const sendEmail = async () => {
    if (!emailTo) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    try {
      setSendingEmail(true);
      const token = localStorage.getItem('admin_token');
      
      // Parse CC and BCC from comma-separated strings
      const ccEmails = emailCc ? emailCc.split(',').map(e => e.trim()).filter(e => e) : [];
      const bccEmails = emailBcc ? emailBcc.split(',').map(e => e.trim()).filter(e => e) : [];
      
      const emailData = {
        to_email: emailTo,
        cc_emails: ccEmails,
        bcc_emails: bccEmails,
        subject: emailSubject,
        html_content: emailBody || `<p>${emailSubject}</p>`,
        quote_id: emailQuoteId,
        rfq_id: selectedRFQ?.id
      };
      
      let endpoint = `${API_URL}/api/admin/email/send`;
      if (emailQuoteId) {
        endpoint = `${API_URL}/api/admin/email/quote/${emailQuoteId}`;
      }
      
      const response = await axios.post(endpoint, emailData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Email sent to ${emailTo}`);
      setShowEmailDialog(false);
      
      // Refresh quotes to show sent status
      if (selectedRFQ) {
        loadExistingQuotes(selectedRFQ.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // View file function
  const viewFile = (file) => {
    if (file.data) {
      setViewingFile(file);
      setShowFileViewer(true);
    } else {
      toast.error('File data not available');
    }
  };

  const createQuote = async () => {
    if (!quotePaymentTerms) {
      toast.error('Please select payment terms');
      return;
    }
    
    const hasPrice = quoteLineItems.some(item => item.unit_price > 0);
    if (!hasPrice) {
      toast.error('Please enter at least one price');
      return;
    }

    try {
      setCreatingQuote(true);
      const token = localStorage.getItem('admin_token');
      
      const quoteData = {
        rfq_id: selectedRFQ.id,
        line_items: quoteLineItems,
        payment_terms: quotePaymentTerms,
        shipping_terms: selectedRFQ.shipping_method || 'Ground',
        validity_days: quoteValidityDays,
        notes: quoteNotes,
        subtotal: calculateSubtotal(),
        shipping_cost: parseFloat(quoteShippingCost) || 0,
        tax: parseFloat(quoteTax) || 0,
        total: calculateTotal()
      };

      const response = await axios.post(`${API_URL}/api/quotes/create`, quoteData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Quote ${response.data.quote.quote_number} created! Status changed to Quoted.`);
      setShowQuoteForm(false);
      setShowDialog(false);
      loadRFQs();
    } catch (error) {
      toast.error('Failed to create quote');
      console.error(error);
    } finally {
      setCreatingQuote(false);
    }
  };

  const sendQuoteEmail = async (quoteId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.put(`${API_URL}/api/quotes/${quoteId}/send`, 
        { quote_id: quoteId, send_to_customer: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadExistingQuotes(selectedRFQ.id);
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  // Open quote viewer (mobile-friendly in-app viewer)
  const openQuoteViewer = async (quoteId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/quotes/${quoteId}/document`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewingQuote(response.data.quote);
      setShowQuoteViewer(true);
    } catch (error) {
      console.error('Quote document error:', error);
      toast.error('Failed to load quote document');
    }
  };

  // Handle print/save PDF from within quote viewer - using iframe for Emergent Preview compatibility
  const handlePrintQuote = () => {
    if (!viewingQuote) return;
    
    // Generate mobile-friendly printable HTML content
    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Quote ${viewingQuote.quote_number}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 15px;
      background: white;
      color: #333;
      font-size: 14px;
      line-height: 1.4;
    }
    .header { border-bottom: 3px solid #007AFF; padding-bottom: 12px; margin-bottom: 15px; }
    .logo { color: #007AFF; font-size: 20px; font-weight: bold; }
    .tagline { color: #666; font-size: 12px; margin-top: 3px; }
    .quote-info { margin-top: 8px; }
    .quote-num { color: #007AFF; font-size: 18px; font-weight: bold; }
    .date-info { color: #666; font-size: 11px; }
    .section { background: #f5f5f5; padding: 12px; border-radius: 6px; margin-bottom: 12px; }
    .section-cyan { background: #e0f7fa; border: 1px solid #00bcd4; }
    .section-title { font-weight: bold; font-size: 11px; text-transform: uppercase; color: #666; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #ddd; }
    .section-title-cyan { color: #00838f; border-color: #00bcd4; }
    .company { font-weight: bold; font-size: 16px; }
    .contact-info { font-size: 13px; margin-top: 4px; }
    .contact-info div { margin: 2px 0; }
    .blue { color: #007AFF; }
    .cyan { color: #00838f; }
    .item-row { background: #f9f9f9; padding: 10px; margin-bottom: 6px; border-radius: 4px; border-left: 3px solid #007AFF; }
    .item-pn { color: #007AFF; font-weight: bold; font-size: 13px; }
    .item-desc { color: #666; font-size: 11px; margin: 4px 0; }
    .item-details { display: flex; justify-content: space-between; font-size: 12px; margin-top: 6px; }
    .item-price { font-weight: bold; color: #007AFF; }
    .totals { text-align: right; padding: 12px; background: #f9f9f9; border-radius: 6px; margin-bottom: 12px; }
    .totals div { margin: 4px 0; font-size: 13px; }
    .total-line { border-top: 2px solid #007AFF; padding-top: 8px; margin-top: 8px; font-size: 18px; color: #007AFF; font-weight: bold; }
    .terms { font-size: 12px; }
    .terms div { margin: 4px 0; }
    .footer { border-top: 2px solid #00bcd4; padding-top: 12px; margin-top: 15px; text-align: center; }
    .guarantee { color: #00838f; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; margin: 2px 0; }
    .thanks { color: #666; font-size: 12px; margin-top: 10px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">Server Memory RFQ</div>
    <div class="tagline">The Best Server Memory Upgrades</div>
    <div class="quote-info">
      <div class="quote-num">${viewingQuote.quote_number}</div>
      <div class="date-info">Date: ${new Date(viewingQuote.created_at).toLocaleDateString()}</div>
      <div class="date-info">Valid Until: ${new Date(viewingQuote.valid_until).toLocaleDateString()}</div>
    </div>
  </div>
  
  <div class="section">
    <div class="section-title">QUOTE FOR</div>
    <div class="company">${viewingQuote.company_name || 'N/A'}</div>
    <div class="contact-info">
      <div>${viewingQuote.contact_name || 'N/A'}</div>
      <div class="blue">${viewingQuote.contact_email || 'N/A'}</div>
      <div>${viewingQuote.contact_phone || 'N/A'}</div>
      ${(() => {
        const s = viewingQuote.shipping_address || {};
        const zip = s.zip || s.zip_code || '';
        const cityState = [s.city, s.state].filter(Boolean).join(', ');
        const hasAny = s.street || s.city || zip || s.country;
        if (!hasAny) return '';
        const esc = (v) => String(v).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
        return `
          <div style="margin-top:6px; padding-top:5px; border-top:1px dashed #ccc; font-size:11px; color:#555;">
            <div style="color:#666; font-size:9px; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:3px;">Ship To</div>
            ${s.street ? `<div>${esc(s.street)}</div>` : ''}
            ${(cityState || zip) ? `<div>${esc([cityState, zip].filter(Boolean).join(' '))}</div>` : ''}
            ${s.country ? `<div>${esc(s.country)}</div>` : ''}
          </div>`;
      })()}
    </div>
  </div>
  
  <div class="section section-cyan">
    <div class="section-title section-title-cyan">YOUR REP</div>
    <div class="company">${companyContact.name}</div>
    <div class="contact-info">
      <div class="cyan">${companyContact.title}</div>
      <div>O: ${companyContact.phone_office}</div>
      <div>D: ${companyContact.phone_direct}</div>
      <div>C: ${companyContact.phone_cell}</div>
      <div class="blue">${companyContact.email}</div>
    </div>
  </div>
  
  <div class="section-title" style="margin-bottom: 8px;">LINE ITEMS</div>
  ${(viewingQuote.line_items || []).map(item => `
    <div class="item-row">
      <div class="item-pn">${item.part_number || 'N/A'}</div>
      <div class="item-desc">${item.description || ''}</div>
      <div class="item-details">
        <span>${item.condition || ''} | Qty: ${item.quoted_qty || 0}</span>
        <span class="item-price">$${(item.unit_price || 0).toFixed(2)} ea = $${(item.line_total || 0).toFixed(2)}</span>
      </div>
    </div>
  `).join('')}
  
  <div class="totals">
    <div>Subtotal: <strong>$${(viewingQuote.subtotal || 0).toFixed(2)}</strong></div>
    <div>Shipping: <strong>$${(viewingQuote.shipping_cost || 0).toFixed(2)}</strong></div>
    <div>Tax: <strong>$${(viewingQuote.tax || 0).toFixed(2)}</strong></div>
    <div class="total-line">TOTAL: $${(viewingQuote.total || 0).toFixed(2)}</div>
  </div>
  
  <div class="section terms">
    <div class="section-title">TERMS</div>
    <div><strong>Payment:</strong> ${viewingQuote.payment_terms || 'N/A'}</div>
    <div><strong>Shipping:</strong> ${viewingQuote.shipping_terms || 'N/A'}</div>
    ${viewingQuote.notes ? `<div><strong>Notes:</strong> ${viewingQuote.notes}</div>` : ''}
  </div>
  
  <div class="footer">
    <div class="guarantee">100% GUARANTEED COMPATIBLE</div>
    <div class="guarantee">QUICK ADVANCE REPLACEMENT</div>
    <div class="guarantee">EXPERT TECHNICAL SUPPORT</div>
    <div class="thanks">Thank you for your business!<br/>Server Memory RFQ</div>
  </div>
</body>
</html>`;
    
    // Create a hidden iframe for printing
    let printFrame = document.getElementById('print-frame');
    if (!printFrame) {
      printFrame = document.createElement('iframe');
      printFrame.id = 'print-frame';
      printFrame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      document.body.appendChild(printFrame);
    }
    
    // Write content to iframe and print
    const frameDoc = printFrame.contentWindow || printFrame.contentDocument;
    const doc = frameDoc.document || frameDoc;
    doc.open();
    doc.write(printContent);
    doc.close();
    
    // Wait for content to load then trigger print
    setTimeout(() => {
      try {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        toast.success('Print dialog should appear');
      } catch (e) {
        console.error('Print failed:', e);
        // Fallback: Use Web Share API
        if (navigator.share) {
          navigator.share({
            title: `Quote ${viewingQuote.quote_number}`,
            text: `Quote from Server Memory RFQ - Total: $${(viewingQuote.total || 0).toFixed(2)}\n\nCompany: ${viewingQuote.company_name}\nContact: ${viewingQuote.contact_name}`
          }).catch(err => console.log('Share cancelled:', err));
        } else {
          toast.error('Print not available - use Safari share button');
        }
      }
    }, 500);
  };

  // Open email dialog from quote viewer
  const openEmailFromViewer = () => {
    if (viewingQuote) {
      setEmailQuoteId(viewingQuote.id);
      setEmailTo(viewingQuote.contact_email || selectedRFQ?.email || '');
      setEmailSubject(`Quote ${viewingQuote.quote_number} from Server Memory RFQ`);
      setEmailBody('');
      setEmailCc('');
      setEmailBcc('');
      setShowEmailDialog(true);
    }
  };

  const downloadQuote = async (quoteId) => {
    // Open the in-app quote viewer instead of trying to open new window
    await openQuoteViewer(quoteId);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      <AdminNav title="RFQ Management" titleTestId="rfq-management-title" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Filters */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <label className="font-['JetBrains_Mono'] text-[#94A3B8] text-sm">Filter by Status:</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-full sm:w-48 bg-transparent border-[#334155]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Quoted">Quoted</SelectItem>
                <SelectItem value="Won">Won</SelectItem>
                <SelectItem value="Lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bulk-delete toolbar */}
        <div className="flex items-center justify-between gap-3 mb-3 bg-[#11141D] border border-[#1E293B] rounded-lg px-4 py-3" data-testid="rfq-bulk-toolbar">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#94A3B8] font-['JetBrains_Mono']">
            <input
              type="checkbox"
              checked={rfqs.length > 0 && selectedIds.size === rfqs.length}
              onChange={toggleSelectAll}
              disabled={rfqs.length === 0}
              className="accent-[#007AFF] w-4 h-4 cursor-pointer"
              data-testid="select-all-rfqs"
            />
            <span>
              {selectedIds.size > 0
                ? <span className="text-[#F59E0B]">{selectedIds.size} selected</span>
                : <span>Select all ({rfqs.length})</span>}
            </span>
          </label>
          <Button
            onClick={() => setConfirmDelete(true)}
            disabled={selectedIds.size === 0}
            className="bg-[#EF4444] hover:bg-[#DC2626] text-white disabled:opacity-30 disabled:cursor-not-allowed"
            size="sm"
            data-testid="bulk-delete-rfqs-btn"
          >
            <Trash size={16} className="mr-1" /> Delete{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </Button>
        </div>

        {/* RFQ Cards - Mobile Optimized */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-12 text-center text-[#94A3B8]">
              <p className="font-['JetBrains_Mono']">Loading RFQs...</p>
            </div>
          ) : rfqs.length === 0 ? (
            <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-12 text-center text-[#94A3B8]">
              <p className="font-['JetBrains_Mono']">No RFQs found</p>
            </div>
          ) : (
            rfqs.map((rfq, index) => {
              const isSelected = rfq.id && selectedIds.has(rfq.id);
              return (
              <div 
                key={rfq.id || index}
                onClick={() => openRFQDialog(rfq)}
                className={`bg-[#11141D] border rounded-lg p-4 sm:p-6 hover:bg-[#1E293B]/30 cursor-pointer transition-colors active:scale-[0.99] ${isSelected ? 'border-[#F59E0B]' : 'border-[#1E293B]'}`}
                data-testid={`rfq-card-${index}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={!!isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => rfq.id && toggleSelect(rfq.id)}
                    className="mt-1 accent-[#007AFF] w-4 h-4 cursor-pointer flex-shrink-0"
                    data-testid={`select-rfq-${index}`}
                    aria-label={`Select RFQ ${rfq.company_name}`}
                  />
                  <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  {/* Main Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-['JetBrains_Mono'] text-lg font-bold text-[#F8FAFC] truncate">
                        {rfq.company_name}
                      </h3>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-['JetBrains_Mono'] ml-2 flex-shrink-0 ${
                        rfq.status === 'Pending' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                        rfq.status === 'Quoted' ? 'bg-[#007AFF]/20 text-[#007AFF]' :
                        rfq.status === 'Won' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        'bg-[#EF4444]/20 text-[#EF4444]'
                      }`}>
                        {rfq.status}
                      </span>
                    </div>
                    
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2 text-[#94A3B8]">
                        <User size={14} className="flex-shrink-0" />
                        <span className="truncate">{rfq.full_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#007AFF]">
                        <EnvelopeSimple size={14} className="flex-shrink-0" />
                        <span className="truncate">{rfq.email}</span>
                      </div>
                      {rfq.phone && (
                        <div className="flex items-center gap-2 text-[#94A3B8]">
                          <PhoneIcon size={14} className="flex-shrink-0" />
                          <span>{rfq.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Memory & Quantity */}
                  <div className="flex flex-row sm:flex-col gap-4 sm:gap-2 sm:text-right border-t sm:border-t-0 pt-3 sm:pt-0 mt-2 sm:mt-0">
                    <div>
                      <p className="text-xs text-[#94A3B8] uppercase">Parts</p>
                      <p className="font-['JetBrains_Mono'] text-[#007AFF] text-sm">
                        {rfq.selected_parts?.length || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8] uppercase">Total Qty</p>
                      <p className="font-['JetBrains_Mono'] text-[#10B981] text-sm">
                        {rfq.selected_parts?.reduce((sum, p) => sum + (p.requested_qty || 0), 0) || rfq.quantity || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8] uppercase">Date</p>
                      <p className="font-['JetBrains_Mono'] text-[#94A3B8] text-sm">
                        {rfq.created_at ? format(new Date(rfq.created_at), 'MMM dd') : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Show first part preview if available */}
                {rfq.selected_parts && rfq.selected_parts.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#1E293B]/50">
                    <p className="text-xs text-[#94A3B8] mb-1">Parts requested:</p>
                    <div className="text-xs font-['JetBrains_Mono'] text-[#007AFF] truncate">
                      {rfq.selected_parts.slice(0, 2).map(p => p.part_number).join(', ')}
                      {rfq.selected_parts.length > 2 && ` +${rfq.selected_parts.length - 2} more`}
                    </div>
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t border-[#1E293B]/50 text-center">
                  <span className="text-xs text-[#007AFF] font-['JetBrains_Mono']">
                    Tap to view full details →
                  </span>
                </div>
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Bulk-delete confirmation dialog */}
      <Dialog open={confirmDelete} onOpenChange={(o) => !deleting && setConfirmDelete(o)}>
        <DialogContent className="bg-[#11141D] border-[#EF4444] text-white max-w-md" data-testid="rfq-bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-[#EF4444] flex items-center gap-2">
              <Trash size={20} /> Delete {selectedIds.size} RFQ{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#94A3B8]">
            This permanently removes the selected RFQ record{selectedIds.size === 1 ? '' : 's'} and any attached quotes.
            This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting} className="border-[#334155]">Cancel</Button>
            <Button onClick={bulkDelete} disabled={deleting} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-bulk-delete-rfqs">
              {deleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RFQ Management Dialog - Full Details */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#11141D] border-[#1E293B] text-[#F8FAFC] max-w-3xl max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-xl sm:text-2xl">
              {selectedRFQ?.company_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRFQ && (
            <div className="space-y-6">
              {/* Contact Details */}
              <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3 flex items-center gap-2">
                  <User size={18} />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Name</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.full_name}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Job Title</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.job_title || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Email</span>
                    <a href={`mailto:${selectedRFQ.email}`} className="text-[#007AFF]">{selectedRFQ.email}</a>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Phone</span>
                    <a href={`tel:${selectedRFQ.phone}`} className="text-[#10B981]">{selectedRFQ.phone}</a>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-[#94A3B8] block text-xs uppercase">Preferred Contact</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.preferred_contact || 'Email'}</span>
                  </div>
                </div>
              </div>

              {/* Company Address */}
              {selectedRFQ.hq_address && (
                <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3 flex items-center gap-2">
                    <MapPin size={18} />
                    Company Address
                  </h4>
                  <div className="text-sm text-[#F8FAFC]">
                    <p>{selectedRFQ.hq_address.street}</p>
                    <p>{selectedRFQ.hq_address.city}, {selectedRFQ.hq_address.state} {selectedRFQ.hq_address.zip}</p>
                    <p>{selectedRFQ.hq_address.country}</p>
                    {selectedRFQ.company_website && (
                      <a href={selectedRFQ.company_website} target="_blank" rel="noopener noreferrer" className="text-[#007AFF] block mt-2">
                        {selectedRFQ.company_website}
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Selected Parts */}
              {partsWithPrices && partsWithPrices.length > 0 && (
                <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3 flex items-center gap-2">
                    <Package size={18} />
                    Selected Parts ({partsWithPrices.length})
                  </h4>
                  <div className="space-y-3">
                    {partsWithPrices.map((part, idx) => (
                      <div key={idx} className="bg-[#05050A]/50 p-3 rounded border border-[#334155]">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-['JetBrains_Mono'] text-[#007AFF] font-bold text-sm truncate">
                              {part.part_number}
                            </p>
                            <p className="text-xs text-[#94A3B8] line-clamp-2">
                              {part.description}
                            </p>
                          </div>
                          {/* Unit Price Badge */}
                          <div className={`px-2 py-1 rounded text-sm font-bold font-['JetBrains_Mono'] ${
                            part.unit_price === 'CALL' || !part.unit_price 
                              ? 'bg-[#F59E0B]/20 text-[#F59E0B]' 
                              : 'bg-[#10B981]/20 text-[#10B981]'
                          }`}>
                            {part.unit_price === 'CALL' || !part.unit_price ? 'CALL' : `$${part.unit_price}`}
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-xs">
                          <div>
                            <span className="text-[#94A3B8] block">Requested</span>
                            <span className="text-[#10B981] font-bold">{part.requested_qty}</span>
                          </div>
                          <div>
                            <span className="text-[#94A3B8] block">Available</span>
                            <span className="text-[#F59E0B]">{part.available_qty_at_selection || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-[#94A3B8] block">Condition</span>
                            <span className="text-[#F8FAFC]">{part.condition}</span>
                          </div>
                          <div>
                            <span className="text-[#94A3B8] block">TAA</span>
                            <span className="text-[#F8FAFC]">{part.taa}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="text-right pt-2 border-t border-[#334155]">
                      <span className="text-[#94A3B8] text-sm">Total Quantity Requested: </span>
                      <span className="text-[#10B981] font-bold font-['JetBrains_Mono']">
                        {partsWithPrices.reduce((sum, p) => sum + (p.requested_qty || 0), 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Fallback to selectedRFQ parts if partsWithPrices not loaded yet */}
              {(!partsWithPrices || partsWithPrices.length === 0) && selectedRFQ.selected_parts && selectedRFQ.selected_parts.length > 0 && (
                <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3 flex items-center gap-2">
                    <Package size={18} />
                    Selected Parts ({selectedRFQ.selected_parts.length})
                  </h4>
                  <div className="space-y-3">
                    {selectedRFQ.selected_parts.map((part, idx) => (
                      <div key={idx} className="bg-[#05050A]/50 p-3 rounded border border-[#334155]">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-['JetBrains_Mono'] text-[#007AFF] font-bold text-sm truncate">
                              {part.part_number}
                            </p>
                            <p className="text-xs text-[#94A3B8] line-clamp-2">
                              {part.description}
                            </p>
                          </div>
                          <div className="px-2 py-1 rounded text-sm font-bold font-['JetBrains_Mono'] bg-[#94A3B8]/20 text-[#94A3B8]">
                            Loading...
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-xs">
                          <div>
                            <span className="text-[#94A3B8] block">Requested</span>
                            <span className="text-[#10B981] font-bold">{part.requested_qty}</span>
                          </div>
                          <div>
                            <span className="text-[#94A3B8] block">Available</span>
                            <span className="text-[#F59E0B]">{part.available_qty_at_selection || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-[#94A3B8] block">Condition</span>
                            <span className="text-[#F8FAFC]">{part.condition}</span>
                          </div>
                          <div>
                            <span className="text-[#94A3B8] block">TAA</span>
                            <span className="text-[#F8FAFC]">{part.taa}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="text-right pt-2 border-t border-[#334155]">
                      <span className="text-[#94A3B8] text-sm">Total Quantity Requested: </span>
                      <span className="text-[#10B981] font-bold font-['JetBrains_Mono']">
                        {selectedRFQ.selected_parts.reduce((sum, p) => sum + (p.requested_qty || 0), 0)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* RFQ Quantity (if no parts selected) */}
              {(!selectedRFQ.selected_parts || selectedRFQ.selected_parts.length === 0) && selectedRFQ.quantity && (
                <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3 flex items-center gap-2">
                    <Package size={18} />
                    Quantity Requested
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#94A3B8] block text-xs uppercase">Quantity</span>
                      <span className="text-[#10B981] font-bold text-lg">{selectedRFQ.quantity}</span>
                    </div>
                    {selectedRFQ.min_quantity && (
                      <div>
                        <span className="text-[#94A3B8] block text-xs uppercase">Min Qty</span>
                        <span className="text-[#F8FAFC]">{selectedRFQ.min_quantity}</span>
                      </div>
                    )}
                    {selectedRFQ.memory_type && (
                      <div>
                        <span className="text-[#94A3B8] block text-xs uppercase">Memory Type</span>
                        <span className="text-[#F8FAFC]">{selectedRFQ.memory_type}</span>
                      </div>
                    )}
                    {selectedRFQ.capacity && (
                      <div>
                        <span className="text-[#94A3B8] block text-xs uppercase">Capacity</span>
                        <span className="text-[#F8FAFC]">{selectedRFQ.capacity}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-[#F59E0B] mt-3">
                    Note: Customer did not select specific parts from inventory. Use "Add Line Item" below to quote manually.
                  </p>
                </div>
              )}

              {/* Uploaded Files / PO Documents */}
              {selectedRFQ.uploaded_files && selectedRFQ.uploaded_files.length > 0 && (
                <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#10B981] mb-3 flex items-center gap-2">
                    <FilePdf size={18} />
                    Attached Documents ({selectedRFQ.uploaded_files.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedRFQ.uploaded_files.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-[#05050A]/50 p-3 rounded border border-[#334155]">
                        <div className="min-w-0 flex-1">
                          <p className="font-['JetBrains_Mono'] text-[#F8FAFC] text-sm truncate">
                            {file.filename}
                          </p>
                          <p className="text-xs text-[#94A3B8]">
                            {file.type} • {Math.round((file.size || 0) / 1024)} KB
                          </p>
                        </div>
                        {file.data ? (
                          <Button
                            size="sm"
                            onClick={() => viewFile(file)}
                            className="bg-[#10B981] hover:bg-[#10B981]/80 text-xs"
                            data-testid={`view-file-${idx}`}
                          >
                            View
                          </Button>
                        ) : (
                          <span className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 px-2 py-1 rounded">
                            No Data
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Server/CPU Info */}
              {(selectedRFQ.server || selectedRFQ.cpu) && (
                <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3">System Info</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[#94A3B8] block text-xs uppercase">Server</span>
                      <span className="text-[#F8FAFC]">{selectedRFQ.server || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-[#94A3B8] block text-xs uppercase">CPU</span>
                      <span className="text-[#F8FAFC]">{selectedRFQ.cpu || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Timing & Urgency */}
              <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-3 text-sm">Timing & Pricing</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Date Needed</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.date_needed || 'Flexible'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Urgency</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.urgency || 'Standard'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Min Qty</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.min_quantity || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Target Price</span>
                    <span className="text-[#10B981] font-bold">{selectedRFQ.target_price || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Repeat Demand</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.repeat_demand || 'One-time'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Partial OK?</span>
                    <span className={selectedRFQ.allow_partial ? 'text-[#10B981]' : 'text-[#F8FAFC]'}>
                      {selectedRFQ.allow_partial ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment & Shipping */}
              <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Payment Method</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.payment_method || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Shipping</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.shipping_method || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Currency</span>
                    <span className="text-[#F8FAFC]">{selectedRFQ.currency || 'USD'}</span>
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              {selectedRFQ.additional_notes && (
                <div className="bg-[#1E293B]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#007AFF] mb-2 text-sm">Additional Notes</h4>
                  <p className="text-[#F8FAFC] text-sm whitespace-pre-wrap">{selectedRFQ.additional_notes}</p>
                </div>
              )}

              {/* Customer Requested Terms - Always show */}
              <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-4 rounded-lg">
                <h4 className="font-['JetBrains_Mono'] text-[#F59E0B] mb-2 text-sm">Customer Requested Terms</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Payment</span>
                    <span className="text-[#F8FAFC] font-bold">{selectedRFQ.payment_method || 'Not specified'}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8] block text-xs uppercase">Shipping</span>
                    <span className="text-[#F8FAFC] font-bold">{selectedRFQ.shipping_method || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Existing Quotes */}
              {existingQuotes.length > 0 && (
                <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-4 rounded-lg">
                  <h4 className="font-['JetBrains_Mono'] text-[#10B981] mb-3 text-sm">Saved Quotes ({existingQuotes.length})</h4>
                  <div className="space-y-2">
                    {existingQuotes.map((quote, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-[#05050A]/50 p-3 rounded border border-[#334155]">
                        <div>
                          <p className="font-['JetBrains_Mono'] text-[#007AFF] text-sm font-bold">{quote.quote_number}</p>
                          <p className="text-xs text-[#94A3B8]">
                            ${quote.total?.toFixed(2)} • {quote.payment_terms} • {quote.status}
                          </p>
                          <p className="text-xs text-[#94A3B8]">
                            Created: {format(new Date(quote.created_at), 'MMM dd, yyyy')}
                          </p>
                          {quote.sent_at && (
                            <p className="text-xs text-[#10B981]">
                              Sent: {format(new Date(quote.sent_at), 'MMM dd, yyyy')}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => downloadQuote(quote.id)}
                            className="bg-[#334155] hover:bg-[#475569] p-2"
                            title="View/Print Quote"
                          >
                            <FilePdf size={16} />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openEmailDialog(quote)}
                            className="bg-[#007AFF] hover:bg-[#007AFF]/80 p-2"
                            title="Email Quote"
                          >
                            <PaperPlaneTilt size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quote Output Form Toggle */}
              <div className="pt-4 border-t border-[#1E293B]">
                <Button
                  onClick={() => setShowQuoteForm(!showQuoteForm)}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/80 font-['JetBrains_Mono']"
                  data-testid="create-quote-btn"
                >
                  <CurrencyDollar className="mr-2" size={20} />
                  {showQuoteForm ? 'Hide Quote Form' : 'Create New Quote'}
                </Button>
              </div>

              {/* Quote Output Form */}
              {showQuoteForm && (
                <div className="bg-[#007AFF]/10 border border-[#007AFF]/30 p-4 rounded-lg space-y-4">
                  <h4 className="font-['JetBrains_Mono'] text-[#007AFF] font-bold flex items-center gap-2">
                    <CurrencyDollar size={20} />
                    Quote Output Form
                  </h4>

                  {/* Line Items with Pricing */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label className="text-[#94A3B8] text-xs uppercase">Line Items</Label>
                      <Button
                        type="button"
                        size="sm"
                        onClick={addLineItem}
                        className="bg-[#334155] hover:bg-[#475569] text-xs h-7 px-2"
                        data-testid="add-line-item-btn"
                      >
                        + Add Item
                      </Button>
                    </div>
                    {quoteLineItems.map((item, idx) => (
                      <div key={idx} className="bg-[#05050A]/50 p-3 rounded border border-[#334155]">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <div className="min-w-0 flex-1 space-y-2">
                            <Input
                              value={item.part_number}
                              onChange={(e) => updateLineItemField(idx, 'part_number', e.target.value)}
                              className="bg-transparent border-[#334155] h-7 text-sm font-['JetBrains_Mono'] text-[#007AFF]"
                              placeholder="Part Number"
                              data-testid={`quote-part-number-${idx}`}
                            />
                            <Input
                              value={item.description}
                              onChange={(e) => updateLineItemField(idx, 'description', e.target.value)}
                              className="bg-transparent border-[#334155] h-7 text-xs"
                              placeholder="Description"
                              data-testid={`quote-description-${idx}`}
                            />
                            <div className="flex gap-2">
                              <select
                                value={item.condition}
                                onChange={(e) => updateLineItemField(idx, 'condition', e.target.value)}
                                className="bg-transparent border border-[#334155] rounded h-7 text-xs px-2 text-[#F8FAFC]"
                              >
                                <option value="NEW">NEW</option>
                                <option value="REF">REF</option>
                                <option value="USED">USED</option>
                              </select>
                              <select
                                value={item.taa}
                                onChange={(e) => updateLineItemField(idx, 'taa', e.target.value)}
                                className="bg-transparent border border-[#334155] rounded h-7 text-xs px-2 text-[#F8FAFC]"
                              >
                                <option value="Yes">TAA: Yes</option>
                                <option value="No">TAA: No</option>
                              </select>
                              {quoteLineItems.length > 1 && (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => removeLineItem(idx)}
                                  className="bg-[#EF4444]/20 hover:bg-[#EF4444]/40 text-[#EF4444] h-7 px-2"
                                >
                                  ✕
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-[#94A3B8]">Qty</Label>
                            <Input
                              type="number"
                              min="0"
                              value={item.quoted_qty}
                              onChange={(e) => updateLineItemQty(idx, e.target.value)}
                              className="bg-transparent border-[#334155] h-8 text-sm"
                              data-testid={`quote-qty-${idx}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#94A3B8]">Unit Price $</Label>
                            {/* Show inventory price hint */}
                            {item.inventory_price && (
                              <div className={`text-xs mb-1 font-mono ${item.inventory_price === 'CALL' ? 'text-[#F59E0B]' : 'text-[#10B981]'}`}>
                                Inv: {item.inventory_price === 'CALL' ? 'CALL' : `$${item.inventory_price}`}
                              </div>
                            )}
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price || ''}
                              onChange={(e) => updateLineItemPrice(idx, e.target.value)}
                              className="bg-transparent border-[#334155] h-8 text-sm"
                              placeholder="0.00"
                              data-testid={`quote-price-${idx}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#94A3B8]">Line Total</Label>
                            <div className="h-8 flex items-center text-[#10B981] font-['JetBrains_Mono'] text-sm">
                              ${item.line_total.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="bg-[#05050A]/50 p-3 rounded border border-[#334155]">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <Label className="text-xs text-[#94A3B8]">Shipping $</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={quoteShippingCost || ''}
                          onChange={(e) => setQuoteShippingCost(e.target.value)}
                          className="bg-transparent border-[#334155] h-8 text-sm"
                          placeholder="0.00"
                          data-testid="quote-shipping"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#94A3B8]">Tax $</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={quoteTax || ''}
                          onChange={(e) => setQuoteTax(e.target.value)}
                          className="bg-transparent border-[#334155] h-8 text-sm"
                          placeholder="0.00"
                          data-testid="quote-tax"
                        />
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#334155] space-y-1 text-right">
                      <div className="text-sm text-[#94A3B8]">
                        Subtotal: <span className="text-[#F8FAFC]">${calculateSubtotal().toFixed(2)}</span>
                      </div>
                      <div className="text-lg font-bold text-[#10B981] font-['JetBrains_Mono']">
                        Total: ${calculateTotal().toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Payment Terms Selection */}
                  <div>
                    <Label className="text-xs text-[#94A3B8] uppercase mb-2 block">Payment Terms (Your Offer)</Label>
                    <Select value={quotePaymentTerms} onValueChange={setQuotePaymentTerms}>
                      <SelectTrigger data-testid="quote-payment-terms" className="bg-transparent border-[#334155]">
                        <SelectValue placeholder="Select payment terms..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TT Wire">TT Wire Transfer</SelectItem>
                        <SelectItem value="Credit Card">Credit Card (link will follow)</SelectItem>
                        <SelectItem value="PO with N30">PO with N30 (pending approval)</SelectItem>
                        <SelectItem value="Crypto - Base Wallet">Crypto — Base / Coinbase Wallet (USDC/ETH)</SelectItem>
                        <SelectItem value="Crypto - MetaMask">Crypto — MetaMask (USDC/ETH)</SelectItem>
                      </SelectContent>
                    </Select>
                    {(quotePaymentTerms === 'Crypto - Base Wallet' || quotePaymentTerms === 'Crypto - MetaMask') && (
                      <div className="mt-2 bg-[#0B1120] border border-[#10B981]/40 rounded p-3 text-xs space-y-1"
                           data-testid="crypto-wallet-info">
                        <p className="text-[#10B981] font-['JetBrains_Mono']">Send to the receiving wallet for the chosen chain:</p>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[#94A3B8]">Base (USDC / ETH):</span>
                          <code className="text-cyan-300 font-mono text-[10px] break-all">0x299290421e696a76ef1efe77adee23c7629613d7</code>
                          <button type="button"
                                  onClick={() => { navigator.clipboard?.writeText('0x299290421e696a76ef1efe77adee23c7629613d7'); toast.success('Base wallet copied'); }}
                                  className="text-[#007AFF] hover:text-white text-[10px] font-['JetBrains_Mono'] whitespace-nowrap"
                                  data-testid="copy-base-wallet">
                            Copy
                          </button>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[#94A3B8]">Ethereum (USDC / ETH):</span>
                          <code className="text-cyan-300 font-mono text-[10px] break-all">0x4A82F8bcDEaBcF56C3bE1f22e5b1B333aFae243f</code>
                          <button type="button"
                                  onClick={() => { navigator.clipboard?.writeText('0x4A82F8bcDEaBcF56C3bE1f22e5b1B333aFae243f'); toast.success('Ethereum wallet copied'); }}
                                  className="text-[#007AFF] hover:text-white text-[10px] font-['JetBrains_Mono'] whitespace-nowrap"
                                  data-testid="copy-eth-wallet">
                            Copy
                          </button>
                        </div>
                        <p className="text-[10px] text-[#64748B] pt-1">
                          Include this link in the quote so the buyer can connect their wallet: <a href="/agents" target="_blank" rel="noreferrer" className="text-[#007AFF] hover:text-white underline">{typeof window !== 'undefined' ? window.location.origin : ''}/agents</a>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Validity & Notes */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-[#94A3B8]">Valid for (days)</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={quoteValidityDays}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setQuoteValidityDays(1);
                          } else {
                            const num = parseInt(val);
                            setQuoteValidityDays(num >= 1 ? num : 1);
                          }
                        }}
                        className="bg-transparent border-[#334155] h-8 text-sm"
                        data-testid="quote-validity"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-[#94A3B8]">Quote Notes</Label>
                    <Textarea
                      value={quoteNotes}
                      onChange={(e) => setQuoteNotes(e.target.value)}
                      className="bg-transparent border-[#334155] text-sm"
                      placeholder="Any additional terms or notes..."
                      rows={2}
                      data-testid="quote-notes"
                    />
                  </div>

                  {/* Generate Quote Button */}
                  <Button
                    onClick={createQuote}
                    disabled={creatingQuote || !quotePaymentTerms}
                    className="w-full bg-[#10B981] hover:bg-[#10B981]/80 font-['JetBrains_Mono']"
                    data-testid="generate-quote-btn"
                  >
                    <FloppyDisk className="mr-2" size={18} />
                    {creatingQuote ? 'Creating Quote...' : 'Generate & Save Quote'}
                  </Button>
                  <p className="text-xs text-[#94A3B8] text-center">
                    This will save the quote, change status to "Quoted", and allow you to email/download it.
                  </p>
                </div>
              )}

              {/* Status Buttons */}
              <div className="space-y-4 pt-4 border-t border-[#1E293B]">
                <p className="font-['JetBrains_Mono'] text-[#94A3B8] text-sm">Update Status:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Button
                    data-testid="status-pending-btn"
                    onClick={() => updateRFQStatus(selectedRFQ.id, 'Pending')}
                    className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 font-['JetBrains_Mono'] text-sm"
                  >
                    Pending
                  </Button>
                  <Button
                    data-testid="status-quoted-btn"
                    onClick={() => updateRFQStatus(selectedRFQ.id, 'Quoted')}
                    className="bg-[#007AFF] hover:bg-[#007AFF]/80 font-['JetBrains_Mono'] text-sm"
                  >
                    Quoted
                  </Button>
                  <Button
                    data-testid="status-won-btn"
                    onClick={() => updateRFQStatus(selectedRFQ.id, 'Won')}
                    className="bg-[#10B981] hover:bg-[#10B981]/80 font-['JetBrains_Mono'] text-sm"
                  >
                    Won
                  </Button>
                  <Button
                    data-testid="status-lost-btn"
                    onClick={() => updateRFQStatus(selectedRFQ.id, 'Lost')}
                    className="bg-[#EF4444] hover:bg-[#EF4444]/80 font-['JetBrains_Mono'] text-sm"
                  >
                    Lost
                  </Button>
                </div>
              </div>

              {/* Reschedule */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 font-['JetBrains_Mono'] text-[#94A3B8] text-sm">
                  <Calendar size={18} />
                  Reschedule Date:
                </label>
                <div className="flex gap-2">
                  <input
                    data-testid="reschedule-date"
                    type="date"
                    value={rescheduleDate}
                    onChange={(e) => setRescheduleDate(e.target.value)}
                    className="flex-1 p-3 bg-transparent border border-[#334155] rounded text-[#F8FAFC] font-['JetBrains_Mono'] text-sm"
                  />
                  <Button
                    data-testid="reschedule-btn"
                    onClick={() => updateRFQStatus(selectedRFQ.id, 'Reschedule')}
                    disabled={!rescheduleDate}
                    className="bg-[#334155] hover:bg-[#475569] font-['JetBrains_Mono'] text-sm"
                  >
                    Set
                  </Button>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 font-['JetBrains_Mono'] text-[#94A3B8] text-sm">
                  <NotePencil size={18} />
                  Add Notes:
                </label>
                <Textarea
                  data-testid="notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-transparent border-[#334155] text-[#F8FAFC] text-sm"
                  placeholder="Add notes about this RFQ..."
                  rows={3}
                />
                <Button
                  data-testid="save-notes-btn"
                  onClick={() => updateRFQStatus(selectedRFQ.id, selectedRFQ.status)}
                  disabled={!notes}
                  className="w-full bg-[#0047AB] hover:bg-[#005DF0] font-['JetBrains_Mono'] text-sm"
                >
                  Save Notes
                </Button>
              </div>

              {/* Notes History */}
              {selectedRFQ.notes_history && selectedRFQ.notes_history.length > 0 && (
                <div className="space-y-2">
                  <p className="font-['JetBrains_Mono'] text-[#94A3B8] text-sm">Notes History:</p>
                  <div className="bg-[#1E293B]/30 p-4 rounded-lg space-y-2 max-h-32 overflow-y-auto">
                    {selectedRFQ.notes_history.map((note, idx) => (
                      <div key={idx} className="border-l-2 border-[#007AFF] pl-3">
                        <p className="text-xs text-[#94A3B8]">
                          {note.timestamp ? format(new Date(note.timestamp), 'MMM dd, yyyy HH:mm') : 'N/A'}
                        </p>
                        <p className="text-[#F8FAFC] text-sm">{note.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-[#11141D] border-[#1E293B] text-[#F8FAFC] max-w-lg mx-4">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-lg flex items-center gap-2">
              <PaperPlaneTilt size={20} className="text-[#007AFF]" />
              {emailQuoteId ? 'Email Quote' : 'Send Email'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[#94A3B8] uppercase">To:</Label>
              <Input
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="bg-transparent border-[#334155]"
                placeholder="recipient@email.com"
                data-testid="email-to"
              />
            </div>
            
            <div>
              <Label className="text-xs text-[#94A3B8] uppercase">CC: (comma separated)</Label>
              <Input
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                className="bg-transparent border-[#334155]"
                placeholder="cc1@email.com, cc2@email.com"
                data-testid="email-cc"
              />
            </div>
            
            <div>
              <Label className="text-xs text-[#94A3B8] uppercase">BCC: (comma separated)</Label>
              <Input
                value={emailBcc}
                onChange={(e) => setEmailBcc(e.target.value)}
                className="bg-transparent border-[#334155]"
                placeholder="bcc@email.com"
                data-testid="email-bcc"
              />
            </div>
            
            <div>
              <Label className="text-xs text-[#94A3B8] uppercase">Subject:</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="bg-transparent border-[#334155]"
                placeholder="Email subject"
                data-testid="email-subject"
              />
            </div>
            
            {!emailQuoteId && (
              <div>
                <Label className="text-xs text-[#94A3B8] uppercase">Message:</Label>
                <Textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="bg-transparent border-[#334155]"
                  placeholder="Your message..."
                  rows={4}
                  data-testid="email-body"
                />
              </div>
            )}
            
            {emailQuoteId && (
              <p className="text-xs text-[#94A3B8] bg-[#1E293B]/50 p-3 rounded">
                The quote details will be automatically included in the email body.
              </p>
            )}
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowEmailDialog(false)}
                variant="outline"
                className="flex-1 border-[#334155]"
              >
                Cancel
              </Button>
              <Button
                onClick={sendEmail}
                disabled={sendingEmail || !emailTo}
                className="flex-1 bg-[#007AFF] hover:bg-[#007AFF]/80"
                data-testid="send-email-btn"
              >
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog open={showFileViewer} onOpenChange={setShowFileViewer}>
        <DialogContent className="bg-[#11141D] border-[#1E293B] text-[#F8FAFC] max-w-3xl max-h-[90vh] mx-4">
          <DialogHeader>
            <DialogTitle className="font-['JetBrains_Mono'] text-lg flex items-center gap-2">
              <FilePdf size={20} className="text-[#10B981]" />
              {viewingFile?.filename}
            </DialogTitle>
          </DialogHeader>
          
          {viewingFile && (
            <div className="overflow-auto max-h-[70vh]">
              {viewingFile.type?.startsWith('image/') ? (
                <img 
                  src={viewingFile.data} 
                  alt={viewingFile.filename}
                  className="max-w-full h-auto mx-auto rounded"
                />
              ) : viewingFile.type === 'application/pdf' ? (
                <iframe
                  src={viewingFile.data}
                  className="w-full h-[60vh]"
                  title={viewingFile.filename}
                />
              ) : (
                <div className="text-center py-8 text-[#94A3B8]">
                  <p>Preview not available for this file type.</p>
                  <a
                    href={viewingFile.data}
                    download={viewingFile.filename}
                    className="text-[#007AFF] underline mt-2 inline-block"
                  >
                    Download File
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Document Viewer Dialog - Mobile Friendly */}
      <Dialog open={showQuoteViewer} onOpenChange={setShowQuoteViewer}>
        <DialogContent className="bg-white text-black max-w-lg max-h-[95vh] overflow-y-auto mx-1 p-0 print:max-w-none print:max-h-none print:overflow-visible">
          {viewingQuote && (
            <>
              {/* Action Buttons */}
              <div className="sticky top-0 bg-white z-10 p-3 border-b border-gray-200 flex flex-wrap gap-2 justify-between items-center print:hidden">
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={async () => {
                      // Download PDF and share it via Web Share API
                      if (!viewingQuote) return;
                      
                      try {
                        toast.loading('Generating PDF...', { id: 'pdf-gen' });
                        const token = localStorage.getItem('admin_token');
                        const response = await fetch(`${API_URL}/api/quotes/${viewingQuote.id}/pdf`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        
                        if (!response.ok) throw new Error('Failed to generate PDF');
                        
                        const blob = await response.blob();
                        const file = new File([blob], `Quote-${viewingQuote.quote_number}.pdf`, { type: 'application/pdf' });
                        
                        toast.dismiss('pdf-gen');
                        
                        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                          // Share the PDF file directly
                          await navigator.share({
                            title: `Quote ${viewingQuote.quote_number}`,
                            text: `Quote from Server Memory RFQ - Total: $${(viewingQuote.total || 0).toFixed(2)}`,
                            files: [file]
                          });
                        } else {
                          // Fallback: download the PDF
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `Quote-${viewingQuote.quote_number}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          toast.success('PDF downloaded!');
                        }
                      } catch (err) {
                        toast.dismiss('pdf-gen');
                        console.error('Share error:', err);
                        toast.error('Could not share PDF');
                      }
                    }}
                    className="bg-[#007AFF] hover:bg-[#007AFF]/80 font-['JetBrains_Mono'] text-xs px-3"
                    data-testid="quote-share-btn"
                  >
                    <PaperPlaneTilt size={14} className="mr-1" />
                    Share PDF
                  </Button>
                  <Button
                    onClick={openEmailFromViewer}
                    className="bg-[#10B981] hover:bg-[#10B981]/80 font-['JetBrains_Mono'] text-xs px-3"
                    data-testid="quote-email-btn"
                  >
                    <PaperPlaneTilt size={14} className="mr-1" />
                    Email
                  </Button>
                </div>
                <Button
                  onClick={() => setShowQuoteViewer(false)}
                  variant="outline"
                  className="border-gray-300 text-gray-600 text-xs px-3"
                >
                  Close
                </Button>
              </div>

              {/* Quote Document Content - Mobile Optimized */}
              <div className="p-4 text-sm" id="quote-printable">
                {/* Header */}
                <div className="mb-4 pb-3 border-b-2 border-[#007AFF]">
                  <h1 className="text-xl font-bold text-[#007AFF]">Server Memory RFQ</h1>
                  <div className="text-gray-600 text-xs">The Best Server Memory Upgrades</div>
                  <div className="mt-2">
                    <div className="text-lg font-bold text-[#007AFF]">{viewingQuote.quote_number}</div>
                    <div className="text-xs text-gray-600">Date: {new Date(viewingQuote.created_at).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-600">Valid Until: {new Date(viewingQuote.valid_until).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Quote For & Contact - Stacked on Mobile */}
                <div className="space-y-3 mb-4">
                  {/* Quote For */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-2">QUOTE FOR</h3>
                    <div className="font-bold text-base">{viewingQuote.company_name || 'N/A'}</div>
                    <div className="text-sm">{viewingQuote.contact_name || 'N/A'}</div>
                    <div className="text-sm text-[#007AFF]">{viewingQuote.contact_email || 'N/A'}</div>
                    <div className="text-sm">{viewingQuote.contact_phone || 'N/A'}</div>
                    {(() => {
                      const s = viewingQuote.shipping_address || {};
                      const zip = s.zip || s.zip_code || '';
                      const cityState = [s.city, s.state].filter(Boolean).join(', ');
                      const hasAny = s.street || s.city || zip || s.country;
                      if (!hasAny) return null;
                      return (
                        <div className="mt-2 pt-2 border-t border-dashed border-gray-300 text-xs text-gray-600" data-testid="quote-ship-to">
                          <div className="uppercase text-[10px] text-gray-500 tracking-wide mb-1">Ship To</div>
                          {s.street && <div>{s.street}</div>}
                          {(cityState || zip) && <div>{[cityState, zip].filter(Boolean).join(' ')}</div>}
                          {s.country && <div>{s.country}</div>}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Company Contact - From Landing Page */}
                  <div className="bg-cyan-50 p-3 rounded-lg border border-cyan-200">
                    <h3 className="font-bold text-cyan-700 text-xs uppercase tracking-wide mb-2">YOUR REP</h3>
                    <div className="font-bold text-base">{companyContact.name}</div>
                    <div className="text-sm text-cyan-600">{companyContact.title}</div>
                    <div className="text-xs mt-1 space-y-0.5">
                      <div>O: {companyContact.phone_office}</div>
                      <div>D: {companyContact.phone_direct}</div>
                      <div>C: {companyContact.phone_cell}</div>
                      <div className="text-[#007AFF]">{companyContact.email}</div>
                    </div>
                  </div>
                </div>

                {/* Line Items - Mobile Friendly Cards */}
                <div className="mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-3">LINE ITEMS</h3>
                  <div className="space-y-2">
                    {(viewingQuote.line_items || []).map((item, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded-lg border-l-4 border-[#007AFF]">
                        <div className="font-bold text-[#007AFF] text-sm">{item.part_number || 'N/A'}</div>
                        <div className="text-gray-600 text-xs mt-1 line-clamp-2">{item.description || ''}</div>
                        <div className="flex justify-between items-center mt-2 text-xs">
                          <span className="text-gray-500">{item.condition || ''} • Qty: {item.quoted_qty || 0}</span>
                          <span className="font-bold text-[#007AFF]">
                            ${(item.unit_price || 0).toFixed(2)} ea = ${(item.line_total || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals - Mobile Friendly */}
                <div className="mb-4">
                  <div className="bg-gray-50 p-3 rounded-lg text-sm">
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">${(viewingQuote.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Shipping:</span>
                      <span className="font-medium">${(viewingQuote.shipping_cost || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-600">Tax:</span>
                      <span className="font-medium">${(viewingQuote.tax || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-t-2 border-[#007AFF] mt-2 text-base font-bold text-[#007AFF]">
                      <span>TOTAL:</span>
                      <span>${(viewingQuote.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="bg-gray-100 p-3 rounded-lg mb-4">
                  <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wide mb-2">TERMS</h3>
                  <div className="space-y-1 text-xs">
                    <div>
                      <span className="text-gray-600">Payment:</span>
                      <span className="font-bold ml-1">{viewingQuote.payment_terms || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Shipping:</span>
                      <span className="font-bold ml-1">{viewingQuote.shipping_terms || 'N/A'}</span>
                    </div>
                  </div>
                  {viewingQuote.notes && (
                    <div className="mt-2 pt-2 border-t border-gray-300 text-xs">
                      <span className="text-gray-600">Notes:</span>
                      <span className="ml-1">{viewingQuote.notes}</span>
                    </div>
                  )}
                </div>

                {/* Guarantees Footer */}
                <div className="border-t-2 border-cyan-400 pt-3 text-center">
                  <div className="text-cyan-600 font-bold text-[10px] uppercase tracking-wide space-y-0.5">
                    {companyContact.guarantees.map((g, idx) => (
                      <div key={idx}>{g}</div>
                    ))}
                  </div>
                  <div className="mt-2 text-gray-500 text-xs">
                    Thank you for your business! • Server Memory RFQ
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRFQs;
